import React, { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

type Phase = 'awaiting-token' | 'ready' | 'submitting' | 'success' | 'invalid';

const ResetPassword: FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('awaiting-token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = i18n.language.startsWith('fr')
      ? 'Nouveau mot de passe — AidFone'
      : 'Set new password — AidFone';
  }, [i18n.language]);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setPhase('ready');
      }
    });

    const hash = window.location.hash;
    const search = window.location.search;

    if (hash.includes('error=') || hash.includes('error_code=')) {
      setPhase('invalid');
      return () => {
        sub.subscription.unsubscribe();
      };
    }

    const looksLikePkce = search.includes('code=');
    const looksLikeImplicit = hash.includes('type=recovery') && hash.includes('access_token=');
    const looksLikeRecovery = looksLikePkce || looksLikeImplicit;

    const timer = window.setTimeout(() => {
      if (looksLikeRecovery) {
        setPhase((current) => (current === 'awaiting-token' ? 'ready' : current));
      } else {
        setPhase((current) => (current === 'awaiting-token' ? 'invalid' : current));
      }
    }, 2500);

    return () => {
      sub.subscription.unsubscribe();
      window.clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(t('resetPassword.errorTooShort', 'Password must be at least 8 characters.'));
      return;
    }
    if (password !== confirm) {
      setError(t('resetPassword.errorMismatch', 'Passwords do not match.'));
      return;
    }

    setPhase('submitting');
    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message || t('resetPassword.errorGeneric', 'Something went wrong. Please try again.'));
      setPhase('ready');
      return;
    }

    // Sign out so the user logs in fresh with the new password — the recovery
    // session shouldn't keep them authenticated.
    await supabase.auth.signOut();

    setPhase('success');
    window.setTimeout(() => navigate('/login', { replace: true }), 2000);
  };

  if (phase === 'awaiting-token') {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4 font-body">
        <div className="text-sm text-brand-text-muted">{t('resetPassword.submitting', 'Updating...')}</div>
      </div>
    );
  }

  if (phase === 'invalid') {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4 font-body">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl border border-brand-border p-8 shadow-sm">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-red-500 text-3xl">link_off</span>
            </div>
            <h1 className="text-xl font-display font-bold text-brand-text mb-2">
              {t('resetPassword.errorInvalidLink', 'This reset link is invalid or has expired. Request a new one.')}
            </h1>
            <Link to="/forgot-password" className="text-brand-primary hover:underline text-sm">
              {t('resetPassword.requestNewLink', 'Request a new link')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'success') {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4 font-body">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl border border-brand-border p-8 shadow-sm">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-emerald-500 text-3xl">check_circle</span>
            </div>
            <h1 className="text-xl font-display font-bold text-brand-text mb-2">
              {t('resetPassword.successTitle', 'Password updated')}
            </h1>
            <p className="text-sm text-brand-text-muted mb-6">
              {t('resetPassword.successBody', 'You can now sign in with your new password.')}
            </p>
            <Link to="/login" className="text-brand-primary hover:underline text-sm">
              {t('resetPassword.goToLogin', 'Sign in')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const submitting = phase === 'submitting';

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4 font-body">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <img src="/images/landing/aidfone-logo-horizontal.png" alt="AidFone" className="h-10" />
        </Link>
        <div className="bg-white rounded-2xl border border-brand-border p-8 shadow-sm">
          <h1 className="text-2xl font-display font-bold text-brand-text mb-1">
            {t('resetPassword.title', 'Choose a new password')}
          </h1>
          <p className="text-sm text-brand-text-muted mb-6">
            {t('resetPassword.subtitle', 'Enter a new password for your account.')}
          </p>

          {error && (
            <div role="alert" className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text">
                {t('resetPassword.newPassword', 'New password')}
              </span>
              <input
                type="password"
                required
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text">
                {t('resetPassword.confirmPassword', 'Confirm new password')}
              </span>
              <input
                type="password"
                required
                autoComplete="new-password"
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={submitting || !password || !confirm}
              className="py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
            >
              {submitting
                ? t('resetPassword.submitting', 'Updating...')
                : t('resetPassword.submit', 'Update password')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
